import React, { FC, MouseEvent, useCallback, useMemo, useState } from 'react';

import { noop } from 'lodash';
import { useIntl } from 'react-intl';

import { ActionButtonVariants } from 'components/action-button';
import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import { IconLoyaltyTiersLock } from 'components/icons/loyalty-tiers-lock';
import { FavoriteLoading } from 'components/lottie-animations/favorite-loading/favorite-loading';
import { LoyaltyTierKey } from 'generated/graphql-gateway';
import { useMediaQuery } from 'hooks/use-media-query';
import { ShareApiData, useShareApi } from 'hooks/use-share-api/use-share-api';
import { ExternalIncentiveModal } from 'pages/loyalty/loyalty-incentives-components/external-incentive-modal';
import { LoyaltyOffersCooldownDrawer } from 'pages/loyalty/loyalty-offers/loyalty-offers-cooldown-drawer';
import { BackIcon } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/BackIcon';
import { CloseIcon } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/CloseIcon';
import { FavouriteIconWrapper as ExternalFavouriteIconWrapper } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/FavouriteIcon';
import { HeartIcon as ExternalHeartIcon } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/HeartIcon';
import {
  LockIcon as ExternalLockIcon,
  LockIconWrapper as ExternalLockIconWrapper,
} from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/LockIcon';
import { ShareIcon as ExternalShareIcon } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/ShareIcon';
import { LabelText as ExternalLabelText } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/loyalty-offers-card.styles';
import { OfferLocked } from 'pages/loyalty/offer-locked';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';

import {
  IncentiveDetailsToast,
  incentiveToast,
} from '../incentive-details/incentive-details-toast';
import {
  ButtonsWrapperV2,
  ContentTitle,
  ContentWrapper,
  BadgeContainer as ExternalBadgeContainer,
  ButtonContainerV2 as ExternalButtonContainerV2,
  Header as ExternalHeader,
  HeaderTitle as ExternalHeaderTitle,
  LeftHeaderButtonsContainer as ExternalLeftHeaderButtonsContainer,
  RightHeaderButtonsContainer as ExternalRightHeaderButtonsContainer,
  ShareIconWrapper as ExternalShareIconWrapper,
  WrapperButtonV2 as ExternalWrapperButtonV2,
  WrapperV2 as ExternalWrapperV2,
  FavouriteButtonWrapper,
  FavouriteIconLoadingWrapper,
  IconWrapper,
  ImageWrapperV2,
  LockOverlay,
  RewardDescriptionV2,
  RewardNameV2,
  RewardPictureLock,
  StyledRewardImageV2,
} from '../incentive-details/incentive-details.styled.default';

import {
  ButtonContainer,
  ButtonsWrapper,
  ErrorMessage,
  StyledActionButton as ExternalStyledActionButton,
  StyledSecondaryActionButton as ExternalStyledSecondaryActionButton,
  LoadingWrapper,
  Padding,
  RewardDescription,
  RewardDetailsContainer,
  RewardImageWrapper,
  RewardName,
  RewardSubtitle,
  StyledIncentiveImage,
  TierRequirementTag,
  TierRequirementTagLocked,
  TierRequirementText,
  TierRequirementTextLocked,
  Wrapper,
  WrapperButton,
} from './external-incentive-details.styled';
import { IExternalIncentiveDetailsViewProps } from './types';

export const ExternalIncentiveDetailsView: FC<IExternalIncentiveDetailsViewProps> = ({
  id,
  title,
  subtitle,
  image,
  description,
  buttonsConfiguration,
  errorMessage,
  offersInCooldown = false,
  favorited = false,
  favoriteLoading = false,
  handleToggleFavorite = noop,
  isOfferClosed,
  externalOfferModal,
  offerTier,
  offerRequiresAuthentication,
  isV2,
  onGoBack,
}) => {
  const { setOriginLocation } = useAuthContext();
  const { formatMessage } = useIntl();
  const { navigate, location } = useLocationContext();
  const { isAuthenticated } = useAuthContext();
  const [modalVisibility, setModalVisibility] = useState(false);
  const cdp = useCdpContext();

  const { getContentForTier } = useFeaturesLoyaltyTiersContent();

  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);

  const pathname = location.pathname;
  const incentiveDetails = {
    title,
    subtitle,
    description,
    image,
    externalOfferModal,
    buttonsConfiguration,
  };

  const modalDetails = {
    title,
    sanityId: id,
    buttonsConfiguration,
    externalOfferModal,
    image,
  };

  const hasSecondaryButton =
    buttonsConfiguration?.secondButton?.buttonPath &&
    buttonsConfiguration?.secondButton?.buttonText;

  const handleFavoriteClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, successCallback?: (message: string) => void) => {
      cdp.logNavigationClick(CustomEventNames.BUTTON_CLICK, {
        Name: 'Favorite',
        Path: pathname,
      });

      event.preventDefault();
      event.stopPropagation();
      if (isV2 && !isAuthenticated()) {
        return navigate(routes.signUp);
      }
      handleToggleFavorite(id, title, successCallback);
    },
    [handleToggleFavorite, id, title, cdp, pathname, isAuthenticated, navigate, isV2]
  );

  const onChangeModalVisibility = useCallback(() => {
    if (!modalVisibility) {
      cdp.trackEvent({
        name: CustomEventNames.EXTERNAL_OFFER_ADDED_TO_ORDER,
        type: EventTypes.Other,
        attributes: {
          sanityId: id,
          name: title,
        },
      });
    }
    setModalVisibility(!modalVisibility);
  }, [modalVisibility, cdp]);

  const handleTermsAndConditionsClick = () => {
    const termsAndConditonsUrl = buttonsConfiguration?.secondButton?.buttonPath?.locale;

    if (!termsAndConditonsUrl) {
      return;
    }

    navigate(termsAndConditonsUrl);
  };

  const routeToSignup = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setOriginLocation(pathname);
    navigate(routes.signUp);
  };

  const offerTierName = useMemo(() => {
    let tierName: LoyaltyTierKey;

    // if there is no tier available in the offer, set Tier_1 as default
    if (!offerTier) {
      tierName = LoyaltyTierKey.TIER_1;
    } else {
      tierName = offerTier;
    }

    // if the offer has the initial tier required we don't want to show the tier name badge
    // unless the offer is locked
    if (tierName === LoyaltyTierKey.TIER_1 && !isOfferClosed) {
      return '';
    }

    return getContentForTier(tierName)?.loyaltyTiersUIConfigName?.locale;
  }, [offerTier, getContentForTier, isOfferClosed]);

  const offerIsNotAvailableToRedeem = offerRequiresAuthentication && !isAuthenticated();

  const { canShare, shareData } = useShareApi();
  const isMobile = useMediaQuery('mobile');

  if (isV2) {
    const isUserAuthenticated = isAuthenticated();

    const handleShareClick = async () => {
      const offerShareData: ShareApiData = {
        text: formatMessage({ id: 'offerDetail', defaultMessage: 'Offer Detail' }),
        title: incentiveDetails.title || '',
        url: location.pathname,
      };

      try {
        await shareData(offerShareData);
      } catch (e) {
        const error = e.toString() || '';
        if (error.includes('AbortError') || error.includes('canceled')) {
          return;
        }
      }
    };

    const renderOfferTag = () => {
      if (isUserAuthenticated && isOfferClosed) {
        return (
          <ExternalBadgeContainer>
            <ExternalLockIconWrapper>
              <ExternalLockIcon width="18.9px" height="14.4px" />
            </ExternalLockIconWrapper>
            <ExternalLabelText>{offerTierName}</ExternalLabelText>
          </ExternalBadgeContainer>
        );
      }

      if (isUserAuthenticated && !isOfferClosed && offerTierName) {
        return (
          <ExternalBadgeContainer>
            <ExternalLabelText>{offerTierName}</ExternalLabelText>
          </ExternalBadgeContainer>
        );
      }

      return null;
    };

    return (
      <ExternalWrapperV2>
        <ExternalHeader>
          <ExternalLeftHeaderButtonsContainer>
            {isMobile ? (
              <IconWrapper onClick={() => onGoBack && onGoBack()}>
                <BackIcon height="1rem" width="1rem" />
              </IconWrapper>
            ) : (
              <IconWrapper onClick={() => onGoBack && onGoBack()}>
                <CloseIcon height="1rem" width="1rem" />
              </IconWrapper>
            )}
          </ExternalLeftHeaderButtonsContainer>
          {isMobile && (
            <ExternalHeaderTitle>
              {formatMessage({ id: 'offerDetail', defaultMessage: 'Offer Detail' })}
            </ExternalHeaderTitle>
          )}
          <ExternalRightHeaderButtonsContainer>
            {enableFavoriteOffers && (
              <ExternalWrapperButtonV2 favoriteLoading={favoriteLoading}>
                <FavouriteButtonWrapper
                  data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
                  onClick={e => {
                    const externalSuccessCb = (message: string) => {
                      incentiveToast(message);
                    };
                    handleFavoriteClick(e, isMobile ? externalSuccessCb : undefined);
                  }}
                  aria-label={formatMessage({
                    id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
                  })}
                  isTransparent={true}
                >
                  {favoriteLoading ? (
                    <FavouriteIconLoadingWrapper>
                      <FavoriteLoading height="2.4rem" width="2.4rem" />
                    </FavouriteIconLoadingWrapper>
                  ) : (
                    <ExternalFavouriteIconWrapper>
                      <ExternalHeartIcon
                        height="1.375rem"
                        width="1.5rem"
                        fillColor={favorited ? '#d62300' : ''}
                      ></ExternalHeartIcon>
                    </ExternalFavouriteIconWrapper>
                  )}
                </FavouriteButtonWrapper>
              </ExternalWrapperButtonV2>
            )}
            {canShare && (
              <ExternalShareIconWrapper onClick={handleShareClick}>
                <ExternalShareIcon height="1.375rem" width="1rem" />
              </ExternalShareIconWrapper>
            )}
          </ExternalRightHeaderButtonsContainer>
        </ExternalHeader>
        <ContentWrapper>
          {!isMobile && (
            <ContentTitle>
              {formatMessage({ id: 'offerDetail', defaultMessage: 'Offer Detail' })}
            </ContentTitle>
          )}
          <ImageWrapperV2>
            {incentiveDetails.image && (
              <StyledRewardImageV2
                alt={title ?? ''}
                image={incentiveDetails.image.locale}
                objectFitContain
                quality={100}
              />
            )}
            {isUserAuthenticated && isOfferClosed && (
              <>
                <LockOverlay />
                <RewardPictureLock fill={primitive.$white} />
              </>
            )}
          </ImageWrapperV2>
          {renderOfferTag()}
          {incentiveDetails.title && <RewardNameV2>{incentiveDetails.title}</RewardNameV2>}
          {incentiveDetails.subtitle && (
            <RewardSubtitle>{incentiveDetails.subtitle.locale}</RewardSubtitle>
          )}
          {incentiveDetails.description && (
            <RewardDescriptionV2 content={incentiveDetails?.description} />
          )}
          {/* padding element needed for safari / iOS native devices */}
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <ButtonsWrapperV2>
            <ExternalButtonContainerV2>
              {!isOfferClosed && !offerIsNotAvailableToRedeem && (
                <>
                  <ExternalStyledActionButton
                    onClick={onChangeModalVisibility}
                    data-testid="redeem-external-v2-offer-btn"
                    eventAttributes={{
                      Name: 'Mobile Order',
                      Path: pathname,
                    }}
                  >
                    {buttonsConfiguration?.firstButton?.buttonText?.locale}
                  </ExternalStyledActionButton>

                  {hasSecondaryButton && (
                    <ExternalStyledSecondaryActionButton
                      onClick={handleTermsAndConditionsClick}
                      variant={ActionButtonVariants.TEXT_ONLY}
                      data-testid="terms-and-conditions-external-v2-offer-btn"
                      eventAttributes={{
                        Name: 'Terms and Conditions',
                        Path: pathname,
                      }}
                    >
                      {buttonsConfiguration?.secondButton?.buttonText?.locale}
                    </ExternalStyledSecondaryActionButton>
                  )}
                </>
              )}
              {isOfferClosed && !offerIsNotAvailableToRedeem && (
                <ExternalStyledActionButton
                  data-testid="order-more-external-v2"
                  to={routes.menu}
                  onClick={() => navigate(routes.menu)}
                >
                  {formatMessage({ id: 'orderMoreToBecomeNextTier' }, { tier: offerTierName })}
                </ExternalStyledActionButton>
              )}
              {offerIsNotAvailableToRedeem && (
                <ExternalStyledActionButton
                  data-testid="sign-up-to-redeem-external-v2"
                  to={routes.signUp}
                  onClick={routeToSignup}
                >
                  {formatMessage({ id: 'signUpToAccessOffer' })}
                </ExternalStyledActionButton>
              )}
            </ExternalButtonContainerV2>
          </ButtonsWrapperV2>
        </ContentWrapper>
        {isMobile && <IncentiveDetailsToast />}
        {modalVisibility && (
          <ExternalIncentiveModal
            onChangeVisibility={onChangeModalVisibility}
            modalVisibility={modalVisibility}
            modalDetails={modalDetails}
            isV2={isV2}
          />
        )}
      </ExternalWrapperV2>
    );
  }

  return (
    <Wrapper>
      <RewardImageWrapper>
        {enableFavoriteOffers && isAuthenticated() && (
          <WrapperButton>
            <ButtonForIconFav
              data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
              onClick={handleFavoriteClick}
              aria-label={formatMessage({
                id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
              })}
              isTransparent={false}
            >
              {favoriteLoading ? (
                <LoadingWrapper>
                  <FavoriteLoading />
                </LoadingWrapper>
              ) : (
                <FavoriteIcon isFavorite={favorited} />
              )}
            </ButtonForIconFav>
          </WrapperButton>
        )}

        {incentiveDetails.image && (
          <StyledIncentiveImage
            alt={title ?? ''}
            image={incentiveDetails.image.locale}
            objectFitContain
            isOfferClosed={isAuthenticated() && isOfferClosed}
          />
        )}
        {isAuthenticated() && isOfferClosed && <OfferLocked />}
      </RewardImageWrapper>
      {isAuthenticated() && isOfferClosed && (
        <TierRequirementTagLocked>
          <IconLoyaltyTiersLock />
          <TierRequirementTextLocked>{offerTierName}</TierRequirementTextLocked>
        </TierRequirementTagLocked>
      )}
      {isAuthenticated() && !isOfferClosed && offerTierName && (
        <TierRequirementTag>
          <TierRequirementText>{offerTierName}</TierRequirementText>
        </TierRequirementTag>
      )}
      <RewardDetailsContainer>
        {incentiveDetails.title && <RewardName>{incentiveDetails.title}</RewardName>}
        {incentiveDetails.subtitle && (
          <RewardSubtitle>{incentiveDetails.subtitle.locale}</RewardSubtitle>
        )}
        {incentiveDetails.description && (
          <RewardDescription content={incentiveDetails?.description} />
        )}
        {/* padding element needed for safari / iOS native devices */}
        <Padding />
      </RewardDetailsContainer>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      <ButtonsWrapper>
        <ButtonContainer>
          {!isOfferClosed && !offerIsNotAvailableToRedeem && (
            <>
              <ExternalStyledActionButton
                onClick={onChangeModalVisibility}
                data-testid="redeem-external-offer-btn"
                eventAttributes={{
                  Name: 'Mobile Order',
                  Path: pathname,
                }}
              >
                {buttonsConfiguration?.firstButton?.buttonText?.locale}
              </ExternalStyledActionButton>

              {hasSecondaryButton && (
                <ExternalStyledSecondaryActionButton
                  onClick={handleTermsAndConditionsClick}
                  variant={ActionButtonVariants.TEXT_ONLY}
                  data-testid="terms-and-conditions-external-offer-btn"
                  eventAttributes={{
                    Name: 'Terms and Conditions',
                    Path: pathname,
                  }}
                >
                  {buttonsConfiguration?.secondButton?.buttonText?.locale}
                </ExternalStyledSecondaryActionButton>
              )}
            </>
          )}
          {isOfferClosed && !offerIsNotAvailableToRedeem && (
            <ExternalStyledActionButton
              data-testid="order-more"
              to={routes.menu}
              onClick={() => navigate(routes.menu)}
            >
              {formatMessage({ id: 'orderMoreToBecomeNextTier' }, { tier: offerTierName })}
            </ExternalStyledActionButton>
          )}
          {offerIsNotAvailableToRedeem && (
            <ExternalStyledActionButton
              data-testid="sign-up-to-redeem"
              to={routes.signUp}
              onClick={routeToSignup}
            >
              {formatMessage({ id: 'signUpToAccessOffer' })}
            </ExternalStyledActionButton>
          )}
        </ButtonContainer>
      </ButtonsWrapper>
      {offersInCooldown && <LoyaltyOffersCooldownDrawer />}
      {modalVisibility && (
        <ExternalIncentiveModal
          onChangeVisibility={onChangeModalVisibility}
          modalVisibility={modalVisibility}
          modalDetails={modalDetails}
        />
      )}
    </Wrapper>
  );
};
